import React, { FunctionComponent } from 'react';
import { Colors } from '../../types/colors';
import { parseTime } from '../../utils/parsingFunctions';
import { Button } from '../button/Button';
import Divider from '../divider/Divider';
import PlaneFlyingIcon from '../icons/PlaneFlyingIcon';
import DangerIcon from '../icons/DangerIcon';
import RefreshIcon from '../icons/RefreshIcon';
import Typography from '../typography/Typography';
import './flightTracker.scss';

export enum FlightStatusEnum {
  SCHEDULED = 'SCHEDULED',
  BOARDING = 'BOARDING',
  OUT_OF_GATE = 'OUT_OF_GATE',
  IN_AIR = 'IN_AIR',
  LANDED = 'LANDED',
  ARRIVED = 'ARRIVED',
  DELAYED = 'DELAYED',
  UNKNOWN = 'UNKNOWN',
  CANCELLED = 'CANCELLED',
}

enum FlightStatusStrings {
  SCHEDULED = 'Scheduled',
  BOARDING = 'Estimated',
  OUT_OF_GATE = 'Estimated',
  IN_AIR = 'Estimated',
  LANDED = 'Landed',
  ARRIVED = 'Arrived',
  DELAYED = 'Delayed',
  UNKNOWN = 'Unknown',
  CANCELLED = 'Cancelled',
}

type FlightTrackerProps = {
  card?: boolean;
  status: FlightStatusEnum;
  estimatedTime: string;
  scheduledTime: string;
  delay?: number;
  flightNumber: string;
  airline?: string;
  city?: string;
  updatedOn: string;
  onRefresh?: () => void;
  isRefreshLoading?: boolean;
  unKnownStatus?: string;
  unKnownText?: string;
  className?: string;
  flightLateText?: string;
  flightEarlyText?: string;
  flightOnTimeText?: string;
}

export const FlightTracker: FunctionComponent<FlightTrackerProps> = ({
  card,
  status,
  estimatedTime,
  scheduledTime,
  delay,
  flightNumber,
  airline,
  city,
  updatedOn = new Date(),
  onRefresh,
  isRefreshLoading,
  className = '',
  unKnownStatus = 'not found',
  unKnownText = 'no info yet',
  flightLateText = 'late',
  flightEarlyText = 'early',
  flightOnTimeText = 'on time',
}) => {
  const baseClass = `${card ? 'flight-tracker-card' : 'flight-tracker-overview'}`;
  const isOnTime = scheduledTime === estimatedTime;
  const todayOrTomorrow = new Date(updatedOn).getDate() === new Date().getDate() ? 'today' : 'yesterday';

  const getColors = () => {
    if (delay || status === FlightStatusEnum.CANCELLED || status === FlightStatusEnum.DELAYED) {
      return { text: Colors.red5, background: 'red' };
    }
    if (status === FlightStatusEnum.UNKNOWN) {
      return { text: Colors.blue5, background: 'blue' };
    }
    return { text: Colors.atlantis5, background: 'green' };
  };

  const colors: {text: Colors, background: string} = getColors();

  return (
    <div className={`flight-tracker ${baseClass} d-flex background-${colors.background} ${className}`}>
      <div className="icon-container">
        <PlaneFlyingIcon color={card ? 'asphalt7' : colors.text} />
      </div>
      <div className={`${baseClass}-content`}>
        <div className="d-flex flex-column flight-info">
          <div className="d-flex">
            <Typography data-testid="journey_flight-number" variant={card ? 'body1' : 'h6'} color={`${card ? 'dark' : colors.text}`}>{flightNumber}</Typography>
            {card && (
              <Typography
                variant="body1"
                color="dark"
                className="m-l-8"
              >
                {`(${city})`}
              </Typography>
            )}
          </div>
          {card && <Typography variant="caption" color="asphalt7" className="airline">{airline}</Typography>}
        </div>

        <div className={`flight-status d-flex ${status === FlightStatusEnum.CANCELLED ? 'row' : ''} ${status === FlightStatusEnum.UNKNOWN ? 'align-end' : ''}`}>
          {status === FlightStatusEnum.UNKNOWN && (
            <>
              <Typography
                variant="button"
                color={colors.text}
                className="m-r-5 status-string"
              >
                {unKnownStatus}
                :
              </Typography>
              <Typography data-testid="journey_flight-status" variant="body1" color={colors.text}>
                {unKnownText}
              </Typography>
            </>
          )}
          {status === FlightStatusEnum.CANCELLED && <DangerIcon color="red5" />}
          {status !== FlightStatusEnum.UNKNOWN
          && (
            <Typography
              variant="button"
              color={colors.text}
              className="m-r-5 status-string"
            >
              {`${FlightStatusStrings[status]}${status !== FlightStatusEnum.CANCELLED ? ':' : ''}`}
            </Typography>
          )}
          {status !== FlightStatusEnum.CANCELLED && status !== FlightStatusEnum.UNKNOWN && (
            <div className="d-flex">
              <Typography
                variant="button"
                color={colors.text}
                className="m-r-5"
              >
                {parseTime(estimatedTime)}
              </Typography>
              <Typography variant="body1" color={colors.text}>
                {isOnTime ? `(${flightOnTimeText})` : Number(delay) > 0 ? `(${delay}min ${flightLateText})` : `(${Math.abs(Number(delay))}min ${flightEarlyText})`}
              </Typography>
            </div>
          )}
        </div>

        {card && (
          <>
            <Divider className="m-t-20 m-b-14" />
            <div className="d-flex align-center">
              <Typography
                variant="body2"
                color="asphalt5"
              >
                {`Updated on ${todayOrTomorrow}: ${parseTime(updatedOn)}`}
              </Typography>
              <Button
                label="refresh"
                onClick={onRefresh}
                variant="info"
                priority="tertiary"
                icon={<RefreshIcon color="blue5" width={17} />}
                iconPosition="left"
                className={`refresh-button ${isRefreshLoading ? 'loading' : ''}`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
